import { Badge } from '@/components/ui/badge'
import { CheckCircle2, XCircle, Clock, ArrowDownLeft, ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism'

export function StatusBadge({ status }: { status: string }) {
  if (status === 'success' || status === 'processed') {
    return (
      <Badge className="bg-green-600 hover:bg-green-700 gap-1.5 pl-1.5 pr-2.5 py-1">
        <CheckCircle2 className="h-3.5 w-3.5" /> Sucesso
      </Badge>
    )
  }
  if (status === 'failed') {
    return (
      <Badge variant="destructive" className="gap-1.5 pl-1.5 pr-2.5 py-1">
        <XCircle className="h-3.5 w-3.5" /> Falha
      </Badge>
    )
  }
  if (status === 'ignored') {
    return (
      <Badge variant="secondary" className="gap-1.5 px-2.5 py-1">
        Ignorado
      </Badge>
    )
  }
  return (
    <Badge variant="secondary" className="gap-1.5 pl-1.5 pr-2.5 py-1">
      <Clock className="h-3.5 w-3.5" /> Pendente
    </Badge>
  )
}

export function DirectionBadge({ direction }: { direction: 'inbound' | 'outbound' }) {
  if (direction === 'inbound') {
    return (
      <Badge
        variant="outline"
        className="gap-1 text-blue-600 border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-800"
      >
        <ArrowDownLeft className="h-3 w-3" />
        Entrada
      </Badge>
    )
  }
  return (
    <Badge
      variant="outline"
      className="gap-1 text-purple-600 border-purple-200 bg-purple-50 dark:bg-purple-950 dark:border-purple-800"
    >
      <ArrowUpRight className="h-3 w-3" />
      Saída
    </Badge>
  )
}

// Ícone circular pequeno (usado no título)
export function DirectionIcon({ direction }: { direction: 'inbound' | 'outbound' }) {
  const isInbound = direction === 'inbound'
  return (
    <div
      className={cn(
        'flex items-center justify-center h-6 w-6 rounded-full',
        isInbound ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-purple-100 dark:bg-purple-900/30'
      )}
    >
      {isInbound ? (
        <ArrowDownLeft className="h-3.5 w-3.5 text-blue-600" />
      ) : (
        <ArrowUpRight className="h-3.5 w-3.5 text-purple-600" />
      )}
    </div>
  )
}

export function JsonViewer({
  data,
  emptyText = '// Vazio',
}: {
  data?: unknown
  emptyText?: string
}) {
  const hasData =
    data && (typeof data === 'string' ? data.length > 0 : Object.keys(data).length > 0)

  if (!hasData) {
    return (
      <div className="bg-muted/30 rounded-lg p-6 border border-dashed border-muted-foreground/30">
        <p className="text-sm text-muted-foreground text-center italic">{emptyText}</p>
      </div>
    )
  }

  const code = typeof data === 'string' ? data : JSON.stringify(data, null, 2)

  return (
    <div className="rounded-lg overflow-hidden border border-zinc-800">
      <SyntaxHighlighter
        language="json"
        style={oneDark}
        customStyle={{
          margin: 0,
          padding: '1rem',
          fontSize: '0.75rem',
          maxHeight: '13rem',
          overflow: 'auto',
          background: '#18181b',
        }}
        wrapLongLines
      >
        {code}
      </SyntaxHighlighter>
    </div>
  )
}

export function HttpCodeBadge({ code }: { code: number }) {
  const isSuccess = code >= 200 && code < 300
  return (
    <span className={cn('font-bold', isSuccess ? 'text-green-600' : 'text-red-600')}>{code}</span>
  )
}
